import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Pencil, Trash2, Plus, Save, X, Search, Hash } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, resolveUrl } from "./api";
import { Btn, Card, Field, Input, Textarea, Select, ImagePicker, ListEditor } from "./ui";

const empty = { name: "", slug: "", logo: "", bonus: 100, players: "", minWithdraw: 100, warning: false, list: "old", downloadUrl: "", seo: { title: "", description: "", keywords: "" }, content: { glance: "", safety: "", verdict: "", downloadIntro: "", reqNote: "", features: [], steps: [], faqs: [] } };

const toForm = (g) => ({
  ...empty, ...g,
  downloadUrl: g.downloadUrl || "",
  seo: { title: "", description: "", keywords: "", ...(g.seo || {}), keywords: Array.isArray(g.seo?.keywords) ? g.seo.keywords.join(", ") : g.seo?.keywords || "" },
  content: { ...empty.content, ...(g.content || {}) },
});

const toBody = (f) => ({
  ...f,
  bonus: Number(f.bonus) || 0,
  minWithdraw: Number(f.minWithdraw) || 100,
  downloadUrl: f.downloadUrl?.trim() || null,
  seo: { ...f.seo, keywords: f.seo.keywords ? f.seo.keywords.split(",").map((k) => k.trim()).filter(Boolean) : [] },
  content: { ...f.content, faqs: (f.content.faqs || []).filter((x) => x.q && x.a) },
});

const FaqEditor = ({ value = [], onChange }) => (
  <div className="space-y-2">
    {value.map((f, i) => (
      <div key={i} className="grid grid-cols-[1fr_auto] gap-2 bg-[#013013] border border-[#1c5f35] rounded-xl p-2">
        <div className="space-y-1.5">
          <Input value={f.q} placeholder={`Question ${i + 1}`} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))} />
          <Textarea rows={2} value={f.a} placeholder="Answer" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} />
        </div>
        <Btn variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))}><X size={15} /></Btn>
      </div>
    ))}
    <Btn variant="green" onClick={() => onChange([...value, { q: "", a: "" }])}><Plus size={15} /> Add FAQ</Btn>
  </div>
);

const GameForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(toForm(initial));
  const [tab, setTab] = useState("basic");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setF((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));
  const setC = (k, v) => setF((p) => ({ ...p, content: { ...p.content, [k]: v } }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = toBody(f);
      const saved = initial.id ? await adminApi.updateGame(initial.id, body) : await adminApi.createGame(body);
      toast.success(initial.id ? "Game updated" : "Game added");
      onSaved(saved);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const tabs = [["basic", "Basic"], ["seo", "SEO"], ["content", "Content"]];
  return (
    <Card title={initial.id ? `Edit: ${initial.name}` : "Add new game"} action={<Btn variant="ghost" onClick={onCancel}><X size={16} /></Btn>} className="border-[#ffb224]/50">
      <div className="flex gap-1 mb-4 bg-[#013013] p-1 rounded-xl w-fit">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" data-testid={`game-form-tab-${k}`} onClick={() => setTab(k)} className={`px-3.5 py-1.5 rounded-lg text-sm font-bold ${tab === k ? "bg-[#ffb224] text-[#062a13]" : "text-emerald-50/70"}`}>{l}</button>
        ))}
      </div>

      {tab === "basic" && (
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Game name"><Input data-testid="game-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Rummy Gold" /></Field>
          <Field label="Slug (URL)" hint="Khali chhodo toh naam se ban jayega"><Input data-testid="game-slug" value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="rummy-gold" /></Field>
          <Field label="Sign-up bonus (₹)"><Input data-testid="game-bonus" type="number" value={f.bonus} onChange={(e) => set("bonus", e.target.value)} /></Field>
          <Field label="Players (text)" hint="e.g. 651.3K"><Input data-testid="game-players" value={f.players} onChange={(e) => set("players", e.target.value)} placeholder="auto" /></Field>
          <Field label="Min. withdrawal (₹)"><Input type="number" value={f.minWithdraw} onChange={(e) => set("minWithdraw", e.target.value)} /></Field>
          <Field label="List"><Select data-testid="game-list" value={f.list} onChange={(e) => set("list", e.target.value)}><option value="old">Old Games (home default)</option><option value="new">New Games</option></Select></Field>
          <div className="sm:col-span-2"><ImagePicker label="Logo" value={f.logo} onChange={(v) => set("logo", v)} testId="game-logo" /></div>
          <div className="sm:col-span-2"><Field label="Download link (is game ke liye alag)" hint="Khali = Settings wala global link use hoga"><Input data-testid="game-download-url" value={f.downloadUrl} onChange={(e) => set("downloadUrl", e.target.value)} placeholder="https://..." /></Field></div>
          <label className="flex items-center gap-2 text-sm text-emerald-50/80 font-semibold"><input type="checkbox" checked={f.warning} onChange={(e) => set("warning", e.target.checked)} className="accent-[#ffb224] w-4 h-4" /> Show "Warning!!" instead of Download</label>
        </div>
      )}

      {tab === "seo" && (
        <div>
          <Field label="Meta title" hint="Khali = auto-generated unique title"><Input data-testid="game-seo-title" value={f.seo.title} onChange={(e) => setSeo("title", e.target.value)} /></Field>
          <Field label="Meta description"><Textarea data-testid="game-seo-desc" value={f.seo.description} onChange={(e) => setSeo("description", e.target.value)} /></Field>
          <Field label="Keywords (comma separated)"><Textarea rows={2} data-testid="game-seo-keywords" value={f.seo.keywords} onChange={(e) => setSeo("keywords", e.target.value)} placeholder="rummy gold apk, rummy gold download" /></Field>
        </div>
      )}

      {tab === "content" && (
        <div>
          <p className="text-xs text-emerald-50/50 mb-3">Khali field = site ka auto-generated unique text dikhega. Jo bharoge wahi overwrite hoga.</p>
          <Field label="At a Glance"><Textarea value={f.content.glance} onChange={(e) => setC("glance", e.target.value)} /></Field>
          <Field label="Playing Safely"><Textarea value={f.content.safety} onChange={(e) => setC("safety", e.target.value)} /></Field>
          <Field label="Should You Try?"><Textarea value={f.content.verdict} onChange={(e) => setC("verdict", e.target.value)} /></Field>
          <Field label="Download intro line"><Input value={f.content.downloadIntro} onChange={(e) => setC("downloadIntro", e.target.value)} /></Field>
          <Field label="Download steps (one per line)"><ListEditor value={f.content.steps} onChange={(v) => setC("steps", v)} placeholder="Step 1..." /></Field>
          <Field label="Key features (one per line)"><ListEditor value={f.content.features} onChange={(v) => setC("features", v)} /></Field>
          <Field label="Requirements note"><Input value={f.content.reqNote} onChange={(e) => setC("reqNote", e.target.value)} /></Field>
          <Field label="FAQs"><FaqEditor value={f.content.faqs} onChange={(v) => setC("faqs", v)} /></Field>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[#1c5f35]">
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn data-testid="game-save" onClick={save} disabled={busy}><Save size={15} /> {busy ? "Saving..." : "Save game"}</Btn>
      </div>
    </Card>
  );
};

export default function GamesTab({ onChanged }) {
  const [games, setGames] = useState([]);
  const [list, setList] = useState("old");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [moveTo, setMoveTo] = useState({});

  const load = async () => setGames(await adminApi.games());
  useEffect(() => { load(); }, []);

  const current = games.filter((g) => g.list === list).sort((a, b) => a.order - b.order);
  const shown = q ? current.filter((g) => g.name.toLowerCase().includes(q.toLowerCase())) : current;

  const persist = async (arr) => {
    setGames((p) => [...p.filter((g) => g.list !== list), ...arr.map((g, i) => ({ ...g, order: i }))]);
    try { await adminApi.reorder(list, arr.map((g) => g.id)); onChanged?.(); } catch (e) { toast.error(errText(e)); load(); }
  };
  const swap = (i, j) => { if (j < 0 || j >= current.length) return; const arr = [...current]; [arr[i], arr[j]] = [arr[j], arr[i]]; persist(arr); };
  const moveToPos = async (g) => {
    const pos = Number(moveTo[g.id]); if (!pos) return;
    try { await adminApi.move(g.id, pos); toast.success(`${g.name} moved to #${pos}`); setMoveTo((m) => ({ ...m, [g.id]: "" })); load(); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };
  const del = async (g) => {
    if (!window.confirm(`Delete "${g.name}" from ${list} list?`)) return;
    try { await adminApi.deleteGame(g.id); toast.success("Deleted"); load(); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4">
      {editing && <GameForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged?.(); }} />}

      <Card
        title={`${list === "old" ? "Old Games" : "New Games"} · ${current.length}`}
        action={
          <div className="flex items-center gap-2">
            <Select data-testid="games-list-select" value={list} onChange={(e) => setList(e.target.value)} className="w-auto py-1.5"><option value="old">Old Games</option><option value="new">New Games</option></Select>
            <Btn data-testid="add-game-btn" onClick={() => setEditing({ ...empty, list })}><Plus size={15} /> Add game</Btn>
          </div>
        }
      >
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-50/40" />
          <Input data-testid="games-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search game..." className="pl-9" />
        </div>
        <ul data-testid="admin-games-list" className="divide-y divide-[#1c5f35]">
          {shown.map((g) => {
            const i = current.indexOf(g);
            return (
              <li key={g.id} data-testid={`admin-game-row-${g.slug}`} className="flex items-center gap-2 sm:gap-3 py-2.5">
                <span className="w-8 text-center text-xs font-extrabold text-[#ffb224]">#{i + 1}</span>
                <img src={resolveUrl(g.logo) || "/images/games/maha-games-logo-w128.webp"} alt="" className="w-10 h-10 rounded-lg object-cover bg-[#013013] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold text-sm truncate">{g.name}</p>
                  <p className="text-[11px] text-emerald-50/50 truncate">₹{g.bonus} · {g.players} · /game/{g.slug}{g.downloadUrl ? " · custom link" : ""}</p>
                </div>
                <div className="hidden sm:flex items-center gap-1">
                  <Input data-testid={`move-input-${g.slug}`} type="number" min={1} value={moveTo[g.id] || ""} onChange={(e) => setMoveTo((m) => ({ ...m, [g.id]: e.target.value }))} placeholder="#" className="w-16 py-1.5 px-2 text-center" />
                  <Btn data-testid={`move-btn-${g.slug}`} variant="green" className="py-1.5 px-2.5" onClick={() => moveToPos(g)} title="Move to position"><Hash size={14} /></Btn>
                </div>
                <Btn data-testid={`up-${g.slug}`} variant="ghost" className="py-1.5 px-2" onClick={() => swap(i, i - 1)} disabled={i === 0}><ArrowUp size={16} /></Btn>
                <Btn data-testid={`down-${g.slug}`} variant="ghost" className="py-1.5 px-2" onClick={() => swap(i, i + 1)} disabled={i === current.length - 1}><ArrowDown size={16} /></Btn>
                <Btn data-testid={`edit-${g.slug}`} variant="green" className="py-1.5 px-2.5" onClick={() => { setEditing(g); window.scrollTo({ top: 0, behavior: "smooth" }); }}><Pencil size={14} /></Btn>
                <Btn data-testid={`delete-${g.slug}`} variant="red" className="py-1.5 px-2.5" onClick={() => del(g)}><Trash2 size={14} /></Btn>
              </li>
            );
          })}
          {shown.length === 0 && <li className="py-8 text-center text-emerald-50/50 text-sm">No games</li>}
        </ul>
      </Card>
    </div>
  );
}
